import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { Tag } from "@xsolla/xui-tag";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import type {
  MultiSelectOption,
  MultiSelectSize,
  MultiSelectVariant,
} from "./types";

export enum DisplayState {
  Placeholder,
  PreRender,
  Calculation,
  Render,
}

interface SavedWidths {
  tagsWidths: Record<string | number, number>;
  countItemWidth: number;
  columnGap: number;
  dependencies: {
    stateList: Array<string | number>;
    variant: MultiSelectVariant;
    size: MultiSelectSize;
    removeTagsButtons: boolean;
  };
}

const COUNT_ITEM_VALUE = "count-item" as const;

interface UseMultiSelectControlProps extends ThemeOverrideProps {
  variant: MultiSelectVariant;
  flexible: boolean;
  size: MultiSelectSize;
  stateList: MultiSelectOption[];
  selectedItems: MultiSelectOption[];
  removeValue: (value: string | number, event?: React.MouseEvent) => void;
  placeholder?: string;
  removeTagsButtons: boolean;
  disabled?: boolean;
  isCanBeCleared?: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useMultiSelectControl = ({
  variant,
  flexible,
  size,
  stateList,
  selectedItems,
  removeValue,
  placeholder,
  removeTagsButtons,
  disabled,
  containerRef,
  themeMode,
  themeProductContext,
}: UseMultiSelectControlProps) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const displayStateRef = useRef<DisplayState>(DisplayState.Placeholder);
  const savedWidthsRef = useRef<SavedWidths | null>(null);

  const itemsRef = useRef<HTMLDivElement>(null);
  const [itemsWidth, setItemsWidth] = useState(0);
  const [selectedContent, setSelectedContent] = useState<ReactNode | null>(
    null
  );

  const isPlaceholder = !selectedItems || selectedItems.length === 0;

  // Observe container width (parent of items, respects width constraints)
  useEffect(() => {
    if (!containerRef.current) return;

    let rafId: number | null = null;

    const syncItemsWidth = () => {
      rafId = null;
      if (!containerRef.current) return;

      const width = containerRef.current.getBoundingClientRect().width;
      setItemsWidth((prev) => (prev === width ? prev : width));
    };

    const scheduleWidthSync = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(syncItemsWidth);
    };

    const resizeObserver = new ResizeObserver(scheduleWidthSync);
    resizeObserver.observe(containerRef.current);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  const widthsDependencies = useMemo(
    () => ({
      stateList: stateList.map((item) => item.value),
      variant,
      size,
      removeTagsButtons,
    }),
    [stateList, variant, size, removeTagsButtons]
  );

  const isWidthsDependenciesChanged = () => {
    const prev = savedWidthsRef.current?.dependencies;
    if (!prev) return true;
    if (prev.variant !== widthsDependencies.variant) return true;
    if (prev.size !== widthsDependencies.size) return true;
    if (prev.removeTagsButtons !== widthsDependencies.removeTagsButtons)
      return true;
    if (prev.stateList.length !== widthsDependencies.stateList.length)
      return true;

    let i = 0;
    for (const v of prev.stateList) {
      if (v !== widthsDependencies.stateList[i]) return true;
      i++;
    }

    return false;
  };

  const setPlaceholder = () => {
    setSelectedContent(
      <Text
        color={theme.colors.control.input.placeholder}
        fontSize={theme.sizing.input(size).fontSize}
      >
        {placeholder}
      </Text>
    );
  };

  const countPossibleItemsCount = (widths: SavedWidths | null) => {
    if (!widths || !selectedItems?.length) return 0;

    const itemsAvailableSpace = itemsRef.current?.clientWidth || 0;
    if (!itemsAvailableSpace) return 0;

    const { tagsWidths, countItemWidth, columnGap } = widths;

    const countPossibleItems = (availableSpace: number) => {
      let remaining = availableSpace;
      let count = 0;
      let isFirst = true;

      for (const item of selectedItems) {
        if (!isFirst) remaining -= columnGap;
        isFirst = false;

        const width = tagsWidths[item.value] ?? 0;
        remaining -= width;

        if (remaining < 0) break;
        count++;
      }

      return count;
    };

    // First try without "+N"
    let possibleCount = countPossibleItems(itemsAvailableSpace);

    // If not all items fit, reserve space for "+N" tag and retry
    if (possibleCount !== selectedItems.length) {
      possibleCount = countPossibleItems(
        itemsAvailableSpace - countItemWidth - columnGap
      );
    }

    return possibleCount;
  };

  const setItemsRender = () => {
    const itemsList =
      displayStateRef.current === DisplayState.PreRender
        ? stateList
        : selectedItems;

    const showAll =
      displayStateRef.current === DisplayState.PreRender || flexible;

    const displayedItemsCount = showAll
      ? itemsList.length
      : countPossibleItemsCount(savedWidthsRef.current);

    // always show at least 1 item
    const displayedItems = itemsList.slice(0, displayedItemsCount || 1);

    const hasHidden =
      displayStateRef.current === DisplayState.PreRender ||
      itemsList.length - displayedItems.length > 0;

    if (hasHidden) {
      const countText =
        displayStateRef.current === DisplayState.PreRender
          ? `+${itemsList.length}`
          : `+${itemsList.length - displayedItems.length}`;

      displayedItems.push({ value: COUNT_ITEM_VALUE, label: countText });
    }

    const content: ReactNode[] = displayedItems.map((item, index) => {
      const isCount = item.value === COUNT_ITEM_VALUE;

      const isReducable =
        (index === 0 &&
          displayStateRef.current === DisplayState.Render &&
          displayedItemsCount === 0) ||
        flexible;

      if (variant === "tag") {
        return (
          <Box
            key={item.value}
            onPress={
              !isCount
                ? (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!disabled) removeValue(item.value, e);
                  }
                : undefined
            }
            style={{
              minWidth: isReducable ? 0 : "fit-content",
              opacity: disabled ? 0.5 : 1,
              cursor: disabled || isCount ? "not-allowed" : "pointer",
            }}
          >
            <Tag
              size={size}
              tone="primary"
              onRemove={
                removeTagsButtons && !isCount
                  ? (e?: React.MouseEvent) => {
                      e?.stopPropagation();
                      if (!disabled) removeValue(item.value, e);
                    }
                  : undefined
              }
            >
              {item.label}
            </Tag>
          </Box>
        );
      }

      // Text variant
      const isCountItem = hasHidden;
      const needsComma =
        index < displayedItems.length - 2 ||
        (index < displayedItems.length - 1 && !isCountItem);

      return (
        <Text
          key={item.value}
          color={theme.colors.content.primary}
          fontSize={theme.sizing.input(size).fontSize}
          numberOfLines={1}
          style={{
            display: "inline",
            whiteSpace: "nowrap",
            overflow: isReducable ? "hidden" : undefined,
            textOverflow: isReducable ? "ellipsis" : undefined,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled || isCount ? "not-allowed" : "default",
          }}
        >
          {item.label}
          {needsComma ? ",\u00A0" : "\u00A0"}
        </Text>
      );
    });

    setSelectedContent(content);

    if (displayStateRef.current === DisplayState.PreRender) {
      displayStateRef.current = DisplayState.Calculation;
    }
  };

  const getWidths = () => {
    if (!itemsRef.current || !containerRef.current) return;

    const allItems = Array.from(itemsRef.current.children) as HTMLElement[];
    if (allItems.length === 0) return;

    const countEl = allItems[allItems.length - 1];
    const countItemWidth = countEl.getBoundingClientRect().width;

    const columnGap =
      parseInt(getComputedStyle(itemsRef.current).columnGap || "0", 10) || 0;

    const tagsWidths: Record<string | number, number> = {};
    let index = 0;

    for (const el of allItems) {
      const key =
        index < allItems.length - 1 ? stateList[index].value : COUNT_ITEM_VALUE;

      tagsWidths[key] = el.getBoundingClientRect().width;
      index++;
    }

    savedWidthsRef.current = {
      tagsWidths,
      countItemWidth,
      columnGap,
      dependencies: widthsDependencies,
    };

    displayStateRef.current = DisplayState.Render;
    setItemsRender();
  };

  // Main flow
  useEffect(() => {
    if (isPlaceholder) {
      displayStateRef.current = DisplayState.Placeholder;
      setPlaceholder();
      return;
    }

    if (flexible) {
      // Flexible mode never needs cached widths
      savedWidthsRef.current = null;
      displayStateRef.current = DisplayState.Render;
      setItemsRender();
      return;
    }

    // Fixed mode: measure if missing or deps changed
    const needsMeasurement =
      !savedWidthsRef.current || isWidthsDependenciesChanged();

    if (needsMeasurement) {
      // Wait for ResizeObserver to set width
      if (itemsWidth === 0) return;

      displayStateRef.current = DisplayState.PreRender;
      setItemsRender(); // render all for measurement
      return;
    }

    displayStateRef.current = DisplayState.Render;
    setItemsRender();
  }, [
    isPlaceholder,
    placeholder,
    flexible,
    disabled,
    selectedItems,
    variant,
    size,
    removeTagsButtons,
    itemsWidth,
    widthsDependencies,
    stateList,
  ]);

  // Measure after PreRender render paints
  useEffect(() => {
    if (displayStateRef.current !== DisplayState.Calculation) return;

    const rafId = requestAnimationFrame(() => {
      if (displayStateRef.current === DisplayState.Calculation) {
        getWidths();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedContent]);

  return {
    itemsRef,
    displayStateRef,
    selectedContent,
    isCalculating:
      displayStateRef.current === DisplayState.PreRender ||
      displayStateRef.current === DisplayState.Calculation,
  };
};
